/**
 * A camada IMPURA que liga o núcleo puro de medição (`medicao-de-voz.js`,
 * [T001]) ao caminho por turnos ([T002], US-1) — relógio real e o estado de
 * QUAL fala, dentro do turno de resposta da IA, é reconhecimento e qual é
 * conteúdo. Extraída de `motor-de-voz.js` (que já tinha estourado o teto de
 * 500 linhas, em 565, mesmo motivo das extrações anteriores — ver o
 * cabeçalho daquele arquivo, "Voz v3 — split de arquivo"): fora da lista de
 * arquivos nomeada na task, mas dentro da regra hard do repo ("arquivo
 * ≤500 linhas — se crescer muito, extraia um módulo irmão").
 *
 * `motor-de-voz.js` reexporta as três funções públicas daqui — os
 * consumidores (o roteiro de medição golden, US-1 AC2, que ainda não existe
 * como task própria) importam de lá, mesmo padrão dos outros módulos
 * irmãos.
 *
 * ## Por que a medição fica DESLIGADA por padrão
 *
 * Nenhum ponto de instrumentação de `motor-de-voz.js` roda em produção por
 * conta própria — só depois que algo chama `iniciarMedicaoDoTurno(...)`
 * (o roteiro golden, nunca o usuário real). Sem essa chamada, `_registroAtivo`
 * é `null` e todo ponto de instrumentação é um `if (!_registroAtivo) return`
 * — leitura de variável + `return`, não um `performance.now()` por turno de
 * usuário real. É isto que faz o custo "sem custar mais que cinquenta
 * milissegundos por turno" (a task) trivial de cumprir: o caminho quente
 * (produção, sem medição) nem chega a chamar o relógio.
 *
 * ## A armadilha que o contrato nomeou (C-14) — como este módulo evita
 *
 * `usecase_ia_crm_chat.py:3077-3082` emite o reconhecimento fixo do
 * `voz_ack` SEMPRE como a 1ª fala do turno — incondicional a `houve_tool` —
 * e ANTES de `executar_turno` (ou seja, antes de qualquer conteúdo real
 * chegar). O evento SSE `fala` (`{texto}`) não carrega nenhuma bandeira que
 * diga "isto é reconhecimento": comparar por TEXTO exigiria duplicar aqui a
 * lista fixa de frases do backend (`FRASES_DE_RECONHECIMENTO`,
 * `voz_ack.py`) — uma segunda fonte da mesma verdade, o tipo de duplicação
 * que o próprio C-14 rejeita.
 *
 * A saída usada aqui é POSICIONAL, não textual: a fila de fala já toca
 * SEQUENCIALMENTE (`fala-da-ia.js::_consumirFilaDeFala`, um `_tocarUmAudio`
 * por vez), e `motorDeVoz.estadoDaFala` marca cada borda de SUBIDA
 * (`!== 'ia'` → `'ia'`) como o início de UM segmento reproduzido. A 1ª borda
 * de subida depois de `fim_da_fala` é SEMPRE o reconhecimento (garantia
 * estrutural do backend, linha acima); a 2ª é a 1ª fala que NÃO é
 * reconhecimento — é isto que `primeiro_audio_de_conteudo`
 * (`contracts/medicao.md`, Iteração 2) mede. O contrato NÃO renomeou as
 * chaves do JSON da `linha()` (o exemplo no topo do documento continua
 * `fim_da_fala__primeiro_audio`/`__primeira_fala_de_progresso` — Iteração 2
 * é revisão de DEFINIÇÃO, não de schema); por isso aqui usamos as marcas do
 * núcleo como já existem em T001 (`MARCA_PRIMEIRA_FALA_DE_PROGRESSO` para o
 * reconhecimento, `MARCA_PRIMEIRO_AUDIO` para o conteúdo), só com a
 * DISCIPLINA de quando cada uma é chamada.
 *
 * ⚠️ **Limite aceito, não escondido:** turnos com `houveTool` cujo progresso
 * (`voz_progresso.py`, fala emitida quando uma tool demora ≥8s) soa uma 3ª
 * frase ANTES da resposta final ficariam com a 2ª borda (a fala de
 * progresso, não o conteúdo de fato) rotulada como `primeiro_audio` — o
 * mesmo problema de fundo do C-14, um segmento mais tarde. Resolver por
 * completo exigiria o backend marcar o evento `fala` com um tipo (mudança
 * de contrato SSE, fora do escopo desta task e dos arquivos permitidos) ou
 * duplicar aqui as listas fixas de `voz_progresso.py` — a duplicação que o
 * C-14 já rejeitou para o caso do reconhecimento. Nomeado, não corrigido: é
 * uma limitação do v1 por turnos que o transporte novo ([T020]+) não herda
 * (lá quem decide o fim de cada fala é o próprio modelo, sem fila de frases
 * fixas por cima).
 */
import {
  criarRegistroDoTurno,
  MARCA_FIM_DA_FALA,
  MARCA_PRIMEIRA_FALA_DE_PROGRESSO,
  MARCA_PRIMEIRO_AUDIO,
  MARCA_RESPOSTA_COMPLETA,
} from './medicao-de-voz'

// Índice (1-based) da fala reproduzida dentro do turno de resposta atual —
// zerado a cada `marcarFimDaFala` (o usuário voltou a falar; uma resposta
// nova está prestes a começar). A 1ª fala é SEMPRE reconhecimento (ver
// cabeçalho); a 2ª é a 1ª de conteúdo. Da 3ª em diante nada é marcado — só a
// PRIMEIRA de cada categoria interessa ao contrato (G2 mede "primeira fala
// que não é reconhecimento").
const INDICE_DA_FALA_DE_RECONHECIMENTO = 1
const INDICE_DA_PRIMEIRA_FALA_DE_CONTEUDO = 2

const MOTIVO_FALHA_STT = 'stt_falhou'

const _relogioPadrao = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

// Não-reativo de propósito (mesmo racional das variáveis de módulo de
// `motor-de-voz.js`) — não é estado de tela, é estado de UM turno medido.
let _registroAtivo = null

/**
 * Ativa a medição do PRÓXIMO turno de Modo de Voz — chamado pelo roteiro
 * golden (US-1 AC2, N=5 execuções), nunca por código de produção.
 *
 * @param {Object} params
 * @param {string} params.transporte `'porTurnos'` | `'tempoReal'`
 * @param {number} params.execucao qual das N execuções do roteiro (1-based)
 * @param {string} params.turno o id do turno no roteiro (ex.: `'T1'`)
 * @param {number} params.idEscritorio escritório de teste da medição
 * @param {number} params.idProfissional profissional de teste da medição
 * @param {boolean} params.houveTool se este turno do roteiro espera tool
 * @param {() => number} [params.agora] relógio injetável — produção usa
 *   `performance.now()` por padrão; testes passam um stub determinístico.
 * @returns {{marcar: Function, marcarFalha: Function, linha: Function}} o
 *   registro do núcleo (`medicao-de-voz.js`), para quem chamou poder ler
 *   `linha()` a qualquer momento sem esperar `encerrarMedicaoDoTurno`.
 */
export const iniciarMedicaoDoTurno = ({
  transporte,
  execucao,
  turno,
  idEscritorio,
  idProfissional,
  houveTool,
  agora,
} = {}) => {
  const registro = criarRegistroDoTurno({
    transporte,
    execucao,
    turno,
    idEscritorio,
    idProfissional,
    houveTool,
  })
  _registroAtivo = {
    registro,
    agora: typeof agora === 'function' ? agora : _relogioPadrao,
    houveTool: Boolean(houveTool),
    indiceDaFalaDoTurno: 0,
    // [spec-008 T030] Só do caminho em TEMPO REAL — ver
    // `marcarPrimeiroAudioDeConteudo`. Zerado junto com o índice a cada
    // `marcarFimDaFala`, porque "primeiro" é por TURNO.
    primeiroAudioMarcado: false,
  }
  return registro
}

/**
 * Devolve a `linha()` do turno ativo (`contracts/medicao.md`) e desativa a
 * medição. `null` se não havia turno ativo — chamar duas vezes seguidas não
 * quebra, só a 1ª devolve algo.
 */
export const encerrarMedicaoDoTurno = () => {
  if (!_registroAtivo) return null
  const linha = _registroAtivo.registro.linha()
  _registroAtivo = null
  return linha
}

/** `true` só durante o roteiro de medição — é o que os pontos de instrumentação de `motor-de-voz.js` consultam antes de fazer qualquer trabalho. */
export const medicaoDoTurnoAtiva = () => Boolean(_registroAtivo)

/**
 * `fim_da_fala` — chamado por `motor-de-voz.js::parar()` no instante em que
 * a gravação do turno do USUÁRIO para. **Não é âncora acústica** (a última
 * amostra de áudio do consultor acima do limiar de energia, como o
 * contrato — Iteração 2, C-14 — pede para o transporte novo): é o instante
 * em que o gesto/VAD que ENCERRA a gravação chega até aqui. No v1 por
 * turnos não existe outra referência disponível sem alterar
 * `captura-de-voz.js` (fora da lista de arquivos desta task). O viés
 * favorece o v1 (o VAD/gesto de parar chega DEPOIS do último som real, então
 * este relógio começa a contar mais tarde do que a âncora acústica
 * contaria — o v1 sempre parece um pouco mais rápido do que é) — nomeado
 * aqui, não escondido.
 */
export const marcarFimDaFala = () => {
  if (!_registroAtivo) return
  _registroAtivo.indiceDaFalaDoTurno = 0
  _registroAtivo.primeiroAudioMarcado = false
  _registroAtivo.registro.marcar(MARCA_FIM_DA_FALA, _registroAtivo.agora())
}

/**
 * [spec-008 T030] `primeiro_audio` para o transporte em TEMPO REAL — a
 * PRIMEIRA fala da IA depois que o turno do consultor fechou.
 *
 * ## Por que não reusar `marcarInicioDeFala`
 *
 * Aquele é POSICIONAL e a posição é um fato do v1: o backend por turnos emite
 * o reconhecimento fixo do `voz_ack` SEMPRE como 1ª fala (garantia estrutural
 * — ver o cabeçalho deste arquivo), então lá a 2ª borda é a 1ª fala de
 * conteúdo. No canal em tempo real **não existe reconhecimento fixo**: sem
 * delegação aberta, a 1ª fala do modelo JÁ É a resposta. Chamar
 * `marcarInicioDeFala` ali descartaria a primeira fala como se fosse
 * reconhecimento e marcaria `primeiro_audio` na SEGUNDA — o transporte novo
 * mediria sistematicamente mais lento do que é, e a comparação da US-7 já
 * viria decidida. É exatamente o viés que a D-6 existe para impedir: os dois
 * transportes chamam as MESMAS MARCAS do núcleo (`medicao-de-voz.js`), com a
 * disciplina de quando cada uma vale em cada caminho.
 *
 * **Uma vez por turno**, como o `index === 2` faz no v1: remarcar num segmento
 * seguinte trocaria o instante do PRIMEIRO áudio pelo de um posterior, e a
 * marca deixaria de ser o que o nome diz. `marcarFimDaFala` rearma.
 *
 * No-op quando nenhuma medição está ativa — o caminho quente de produção não
 * chega a ler o relógio.
 */
export const marcarPrimeiroAudioDeConteudo = () => {
  if (!_registroAtivo) return
  if (_registroAtivo.primeiroAudioMarcado) return
  _registroAtivo.primeiroAudioMarcado = true
  _registroAtivo.registro.marcar(MARCA_PRIMEIRO_AUDIO, _registroAtivo.agora())
}

/**
 * Uma fala da IA começou a tocar — borda de SUBIDA de
 * `motorDeVoz.estadoDaFala` para `'ia'`. "Reproduzida" aqui quer dizer: o
 * instante em que `fala-da-ia.js::_tocarUmAudio` liga `estadoDaFala`, que é
 * o ponto mais próximo do alto-falante que este módulo alcança sem tocar
 * `fala-da-ia.js` (fora da lista de arquivos desta task) — não é o evento
 * `ended`/rede, é o começo real da reprodução (`audio.play()` é chamado
 * logo em seguida, síncrono).
 *
 * A 1ª chamada depois de `marcarFimDaFala` é SEMPRE o reconhecimento fixo
 * do `voz_ack` (garantia estrutural do backend, ver cabeçalho do arquivo) —
 * só vira marca no núcleo quando `houveTool` (o contrato descreve esta
 * marca como obrigatória "quando houve tool"; sem tool o reconhecimento
 * ainda toca — o backend não sabe de antemão que não vai chamar nenhuma —
 * mas não entra na linha, porque não é o que o roteiro está medindo nesse
 * turno). A 2ª chamada é a 1ª fala que NÃO é reconhecimento — vira
 * `MARCA_PRIMEIRO_AUDIO`, sempre (com ou sem tool).
 */
export const marcarInicioDeFala = () => {
  if (!_registroAtivo) return
  _registroAtivo.indiceDaFalaDoTurno += 1
  const instante = _registroAtivo.agora()
  if (_registroAtivo.indiceDaFalaDoTurno === INDICE_DA_FALA_DE_RECONHECIMENTO) {
    if (_registroAtivo.houveTool) {
      _registroAtivo.registro.marcar(MARCA_PRIMEIRA_FALA_DE_PROGRESSO, instante)
    }
    return
  }
  if (_registroAtivo.indiceDaFalaDoTurno === INDICE_DA_PRIMEIRA_FALA_DE_CONTEUDO) {
    _registroAtivo.registro.marcar(MARCA_PRIMEIRO_AUDIO, instante)
  }
}

/**
 * Uma fala da IA terminou de tocar — borda de DESCIDA de `estadoDaFala`
 * desde `'ia'`. Remarca `resposta_completa` a CADA fala que termina;
 * "última chamada vence" (política já documentada em
 * `medicao-de-voz.js::marcar`) faz o valor final ficar com o instante da
 * ÚLTIMA fala do turno, sem este módulo precisar saber de antemão qual
 * segmento é o último — ele só sabe depois que a fila esvazia de vez.
 */
export const marcarFimDeFalaAtual = () => {
  if (!_registroAtivo) return
  _registroAtivo.registro.marcar(MARCA_RESPOSTA_COMPLETA, _registroAtivo.agora())
}

/**
 * N10 (núcleo, `medicao-de-voz.js`): turno que falhou NUNCA some da
 * medição. Chamado pelo caminho de falha REAL de STT/rede de
 * `motor-de-voz.js` (nunca pelos desfechos `descartado_curto`/
 * `descartado_sem_fala`, que não são falha — são "o usuário não disse
 * nada", contrato do backend já documentado em `motor-de-voz.js`).
 *
 * [spec-008 T030] `motivo` passou a ser PARÂMETRO, com o default de sempre.
 * O caminho por turnos continua chamando sem argumento e gravando
 * `'stt_falhou'`; o transporte em tempo real falha por outras causas
 * (`canal_caiu`, `erro_do_provedor`) e gravá-las como STT seria uma linha
 * MENTIROSA no artefato da US-7 — pior do que linha nenhuma, porque parece
 * dado. N10 exige motivo NOMEADO, não um motivo qualquer.
 *
 * @param {string} [motivo] motivo nomeado da falha; default `'stt_falhou'`.
 */
export const marcarFalhaDaMedicao = (motivo = MOTIVO_FALHA_STT) => {
  if (!_registroAtivo) return
  _registroAtivo.registro.marcarFalha(motivo)
}
